import React, { useContext, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  Check,
  DollarSign,
  Edit,
  Eye,
  History,
  RefreshCw,
  Trash2,
} from 'lucide-react';
import threeDotsIcon from '../../../../../assets/three-dots-icon.svg';
import ConfirmationModal from '../../../../../components/ModalConfirmation';
import { AuditHistoryModal } from '../../../../../components/StandardPage';
import { FeedbackContext } from '../../../../../contexts/FeedbackContext';
import { financialEntryApi, financialRecurrenceApi } from '../../api';
import { financialEntryRoutes } from '../../routes';
import { FinancialEntryListRow } from '../../types';
import {
  FINANCIAL_ENTRY_STATUS,
  FINANCIAL_ENTRY_STATUS_OPTIONS,
} from '../../constants';
import StatusBadge from '../StatusBadge';
import EditRecurrenceModal from '../../EditRecurrenceModal';
import {
  DropdownButton,
  DropdownContainer,
  DropdownIcon,
  DropdownItem,
  DropdownMenu,
  DropdownMenuPanel,
  SubMenu,
  SubMenuItem,
} from './style';

const MAIN_MENU_WIDTH = 200;
const SUB_MENU_WIDTH = 190;
const MENU_GAP = 4;

interface ActionsDropdownProps {
  entry: FinancialEntryListRow;
  onUpdated: () => void;
  onView: (entryId: number) => void;
}

const ActionsDropdown: React.FC<ActionsDropdownProps> = ({ entry, onUpdated, onView }) => {
  const navigate = useNavigate();
  const { setFeedback } = useContext(FeedbackContext);
  const entryId = entry.entryId ?? entry.id;
  const isVirtual = Boolean(entry.virtual);
  const recurrenceId = entry.recurrenceId ?? null;
  const occurrenceDate = entry.occurrenceDate
    ? entry.occurrenceDate.slice(0, 10)
    : null;
  const canScopeRecurrence = recurrenceId != null && occurrenceDate != null;

  const [isOpen, setIsOpen] = useState(false);
  const [showStatusSubmenu, setShowStatusSubmenu] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditScopeModal, setShowEditScopeModal] = useState(false);
  const [showDeleteScopeModal, setShowDeleteScopeModal] = useState(false);
  const [showRecurrenceEditModal, setShowRecurrenceEditModal] = useState(false);
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 200,
    direction: 'down' as 'up' | 'down',
  });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const updateDropdownPosition = (submenuOpen: boolean) => {
    if (!buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();
    const totalWidth = submenuOpen
      ? MAIN_MENU_WIDTH + SUB_MENU_WIDTH + MENU_GAP
      : MAIN_MENU_WIDTH;
    const menuHeight = submenuOpen ? 240 : 280;
    const spaceBelow = window.innerHeight - rect.bottom;
    const direction =
      spaceBelow < menuHeight && rect.top > menuHeight ? 'up' : 'down';

    setDropdownPosition({
      top: direction === 'up' ? rect.top - menuHeight - 4 : rect.bottom + 4,
      left: Math.max(8, rect.right - totalWidth),
      width: totalWidth,
      direction,
    });
  };

  useEffect(() => {
    if (!isOpen) return;
    updateDropdownPosition(showStatusSubmenu);
  }, [isOpen, showStatusSubmenu]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        buttonRef.current?.contains(target) ||
        dropdownRef.current?.contains(target)
      ) {
        return;
      }
      setIsOpen(false);
      setShowStatusSubmenu(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleToggle = () => {
    if (!isOpen && buttonRef.current) {
      updateDropdownPosition(false);
    }
    setIsOpen((o) => !o);
    if (isOpen) setShowStatusSubmenu(false);
  };

  const notifyError = (err: unknown, fallback: string) => {
    const message =
      (err as any)?.response?.data?.message || fallback;
    setFeedback({ message, isOpen: true, statusOk: false });
  };

  const materializeOccurrence = (cancel = false) =>
    financialRecurrenceApi.materialize(recurrenceId!, {
      occurrenceDate: occurrenceDate!,
      ...(cancel ? { cancel: true } : {}),
    });

  const handleMaterialize = async () => {
    try {
      setIsLoading(true);
      await materializeOccurrence();
      setFeedback({
        message: 'Lançamento efetivado com sucesso.',
        isOpen: true,
        statusOk: true,
      });
      setIsOpen(false);
      onUpdated();
    } catch (err) {
      notifyError(err, 'Erro ao efetivar o lançamento.');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePayVirtual = async () => {
    try {
      setIsLoading(true);
      const detail = await materializeOccurrence();
      await financialEntryApi.update(detail.id, {
        status: FINANCIAL_ENTRY_STATUS.PAID,
      });
      setFeedback({
        message: 'Lançamento pago com sucesso.',
        isOpen: true,
        statusOk: true,
      });
      setIsOpen(false);
      onUpdated();
    } catch (err) {
      notifyError(err, 'Erro ao pagar o lançamento.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditOnlyThis = async () => {
    if (!isVirtual) {
      setShowEditScopeModal(false);
      navigate(financialEntryRoutes.edit(entryId));
      return;
    }

    try {
      setIsLoading(true);
      const detail = await materializeOccurrence();
      setShowEditScopeModal(false);
      navigate(financialEntryRoutes.edit(detail.id));
    } catch (err) {
      notifyError(err, 'Erro ao preparar a edição.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditFromHere = () => {
    setShowEditScopeModal(false);
    setShowRecurrenceEditModal(true);
  };

  const handleDeleteOnlyThis = async () => {
    try {
      setIsLoading(true);
      if (isVirtual) {
        await materializeOccurrence(true);
      } else {
        await financialEntryApi.remove(entryId);
      }
      setFeedback({
        message: 'Lançamento excluído com sucesso.',
        isOpen: true,
        statusOk: true,
      });
      setShowDeleteScopeModal(false);
      onUpdated();
    } catch (err) {
      notifyError(err, 'Erro ao excluir lançamento.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteFromHere = async () => {
    try {
      setIsLoading(true);
      await financialRecurrenceApi.endFrom(recurrenceId!, occurrenceDate!);
      if (!isVirtual) {
        await financialEntryApi.remove(entryId);
      }
      setFeedback({
        message: 'Recorrência encerrada a partir desta ocorrência.',
        isOpen: true,
        statusOk: true,
      });
      setShowDeleteScopeModal(false);
      onUpdated();
    } catch (err) {
      notifyError(err, 'Erro ao encerrar a recorrência.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = async (status: number) => {
    try {
      setIsLoading(true);
      await financialEntryApi.update(entryId, { status });
      setFeedback({
        message: 'Status atualizado com sucesso.',
        isOpen: true,
        statusOk: true,
      });
      setIsOpen(false);
      setShowStatusSubmenu(false);
      onUpdated();
    } catch {
      setFeedback({
        message: 'Erro ao atualizar status.',
        isOpen: true,
        statusOk: false,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      setIsLoading(true);
      await financialEntryApi.remove(entryId);
      setFeedback({
        message: 'Lançamento excluído com sucesso.',
        isOpen: true,
        statusOk: true,
      });
      setShowDeleteModal(false);
      onUpdated();
    } catch {
      setFeedback({
        message: 'Erro ao excluir lançamento.',
        isOpen: true,
        statusOk: false,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const virtualMenu = (
    <DropdownMenuPanel>
      <DropdownItem onClick={() => !isLoading && handleMaterialize()}>
        <Check size={16} />
        Efetivar lançamento
      </DropdownItem>
      <DropdownItem onClick={() => !isLoading && handlePayVirtual()}>
        <DollarSign size={16} />
        Pagar
      </DropdownItem>
      <DropdownItem
        onClick={() => {
          setIsOpen(false);
          setShowEditScopeModal(true);
        }}
      >
        <Edit size={16} />
        Editar
      </DropdownItem>
      <DropdownItem
        onClick={() => {
          setIsOpen(false);
          setShowDeleteScopeModal(true);
        }}
      >
        <Trash2 size={16} />
        Excluir
      </DropdownItem>
    </DropdownMenuPanel>
  );

  const regularMenu = (
    <DropdownMenuPanel>
      <DropdownItem
        onClick={() => {
          setIsOpen(false);
          onView(entryId);
        }}
      >
        <Eye size={16} />
        Visualizar
      </DropdownItem>
      <DropdownItem
        onClick={() => {
          setIsOpen(false);
          if (canScopeRecurrence) {
            setShowEditScopeModal(true);
          } else {
            navigate(financialEntryRoutes.edit(entryId));
          }
        }}
      >
        <Edit size={16} />
        Editar
      </DropdownItem>
      <DropdownItem
        $active={showStatusSubmenu}
        onClick={() => setShowStatusSubmenu((s) => !s)}
      >
        <RefreshCw size={16} />
        Alterar status
      </DropdownItem>
      <DropdownItem
        onClick={() => {
          setIsOpen(false);
          setShowAuditModal(true);
        }}
      >
        <History size={16} color="#2563eb" />
        Auditoria
      </DropdownItem>
      <DropdownItem
        onClick={() => {
          setIsOpen(false);
          if (canScopeRecurrence) {
            setShowDeleteScopeModal(true);
          } else {
            setShowDeleteModal(true);
          }
        }}
      >
        <Trash2 size={16} />
        Excluir
      </DropdownItem>
    </DropdownMenuPanel>
  );

  return (
    <>
      <DropdownContainer>
        <DropdownButton ref={buttonRef} type="button" onClick={handleToggle}>
          <DropdownIcon src={threeDotsIcon} alt="Ações" />
        </DropdownButton>
      </DropdownContainer>

      {isOpen &&
        ReactDOM.createPortal(
          <DropdownMenu
            ref={dropdownRef}
            top={dropdownPosition.top}
            left={dropdownPosition.left}
            width={dropdownPosition.width}
            direction={dropdownPosition.direction}
          >
            {!isVirtual && showStatusSubmenu && (
              <SubMenu>
                {FINANCIAL_ENTRY_STATUS_OPTIONS.map((opt) => (
                  <SubMenuItem
                    key={opt.value}
                    onClick={() => !isLoading && handleStatusChange(opt.value)}
                  >
                    <StatusBadge status={opt.value} />
                  </SubMenuItem>
                ))}
              </SubMenu>
            )}

            {isVirtual ? virtualMenu : regularMenu}
          </DropdownMenu>,
          document.body,
        )}

      <ConfirmationModal
        isOpen={showDeleteModal}
        title="Excluir lançamento"
        message={`Tem certeza que deseja excluir o lançamento #${entryId}?`}
        actions={[
          {
            label: 'Cancelar',
            onClick: () => setShowDeleteModal(false),
            variant: 'outline',
          },
          {
            label: isLoading ? 'Excluindo...' : 'Excluir',
            onClick: handleDelete,
            variant: 'danger',
          },
        ]}
      />

      <ConfirmationModal
        isOpen={showEditScopeModal}
        title="Editar lançamento recorrente"
        message="Este lançamento faz parte de uma recorrência. O que deseja editar?"
        actions={[
          {
            label: 'Cancelar',
            onClick: () => setShowEditScopeModal(false),
            variant: 'outline',
          },
          {
            label: isLoading ? 'Aguarde...' : 'Apenas este',
            onClick: () => !isLoading && handleEditOnlyThis(),
            variant: 'primary',
          },
          {
            label: 'Este e os próximos',
            onClick: handleEditFromHere,
            variant: 'primary',
          },
        ]}
      />

      <ConfirmationModal
        isOpen={showDeleteScopeModal}
        title="Excluir lançamento recorrente"
        message="Este lançamento faz parte de uma recorrência. O que deseja excluir?"
        actions={[
          {
            label: 'Cancelar',
            onClick: () => setShowDeleteScopeModal(false),
            variant: 'outline',
          },
          {
            label: isLoading ? 'Aguarde...' : 'Apenas este',
            onClick: () => !isLoading && handleDeleteOnlyThis(),
            variant: 'danger',
          },
          {
            label: 'Este e os próximos',
            onClick: () => !isLoading && handleDeleteFromHere(),
            variant: 'danger',
          },
        ]}
      />

      {showRecurrenceEditModal && canScopeRecurrence && (
        <EditRecurrenceModal
          recurrenceId={recurrenceId!}
          effectiveFrom={occurrenceDate!}
          onClose={() => setShowRecurrenceEditModal(false)}
          onUpdated={onUpdated}
        />
      )}

      {showAuditModal && (
        <AuditHistoryModal
          tableName="FinancialEntry"
          recordId={entryId}
          recordTitle={`Lançamento #${entryId}`}
          onClose={() => setShowAuditModal(false)}
        />
      )}
    </>
  );
};

export default ActionsDropdown;
